import {detailServicesPath} from "../../Constants";
import axios from "axios";
import {getUid} from "../../utils/anonymousUid";
import {
    parseCatalogItemGetResponse,
    resolveCatalogItemCompositionFromGet,
    resolveCustomerCompositionSceneFrom3dGet,
    syncStoredUidFromCustomerResponse,
} from "../../utils/compositionMediaUtils";
import {collectTexturedPicturesFromCustomer3dGet} from "../../utils/customerComposition3dTextures";

const restUrl = detailServicesPath + '/customers/compositions'

export default class CustomerCompositionController {
    static async get(typeComposition, cornerType, pageSize, page, filters = {}) {
        const {
            search,
            model,
            top,
            discount,
            sort,
            priceFrom,
            priceTo,
            sizeLeftFrom,
            sizeLeftTo,
            sizeRightFrom,
            sizeRightTo,
        } = filters;

        const params = {
            typeComposition: typeComposition,
            cornerType: cornerType, //"ALL|CORNER|STRAIGHT"
            pageSize: pageSize,
            page: page,
            search: search,
            model: model,
        };

        if (top) {
            params.top = true;
        }
        if (discount) {
            params.discount = true;
        }
        if (sort) {
            params.sort = sort;
        }
        if (priceFrom !== undefined && priceFrom !== null && priceFrom !== "") {
            params.startPrice = priceFrom;
        }
        if (priceTo !== undefined && priceTo !== null && priceTo !== "") {
            params.endPrice = priceTo;
        }
        if (sizeLeftFrom !== undefined && sizeLeftFrom !== null && sizeLeftFrom !== "") {
            params.startLengthLeft = sizeLeftFrom;
        }
        if (sizeLeftTo !== undefined && sizeLeftTo !== null && sizeLeftTo !== "") {
            params.endLengthLeft = sizeLeftTo;
        }
        if (sizeRightFrom !== undefined && sizeRightFrom !== null && sizeRightFrom !== "") {
            params.startLengthRight = sizeRightFrom;
        }
        if (sizeRightTo !== undefined && sizeRightTo !== null && sizeRightTo !== "") {
            params.endLengthRight = sizeRightTo;
        }

        const queryString = Object.keys(params)
            .filter(key => params[key] !== undefined && params[key] !== null)
            .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
            .join('&');

        const url = `${restUrl}/filter?${queryString}`;

        return axios.get(url);
    }

    static async getDrafts() {
        return axios.get(`${restUrl}/drafts`, {
            params: {
                key: getUid(),
            },
            headers: {
                'Content-Type': 'application/json',
            },
        });
    }

    static async deleteDraft(id) {
        return axios.delete(`${restUrl}/${id}`, {
            params: {
                key: getUid(),
            },
            headers: {
                'Content-Type': 'application/json',
            },
        });
    }


    static async getById(id, {catalogItemPage = false} = {}) {
        const response = await axios.get(restUrl + "/" + id, {
            params: {
                uuid: getUid(),
            },
            headers: {
                'Content-Type': 'application/json',
                ...(catalogItemPage ? {catalogItemRequest: 'true'} : {}),
            },
        });
        const data = response.data;
        const isTemplateDraftPayload = data != null && (
            Array.isArray(data)
            || (typeof data === "object" && (
                "first" in data
                || "second" in data
                || "template" in data
                || "draft" in data
            ))
        );

        if (isTemplateDraftPayload) {
            const {template, draft, actualPrice} = parseCatalogItemGetResponse(data);
            if (catalogItemPage) {
                response.data = {template, draft, actualPrice};
            } else {
                const {composition} = resolveCatalogItemCompositionFromGet(template, draft);
                response.data = composition ?? template;
            }
        }

        return response;
    }

    /** GET …/compositions/{originalId}/3d — сцена заказчика (draft — query `id`, как у module POST). */
    static async get3dSceneComposition(
        originalId,
        {catalogItemPage = false, draftCompositionId = null} = {},
    ) {
        const params = {
            uuid: getUid(),
        };
        if (draftCompositionId != null && draftCompositionId !== "") {
            params.id = draftCompositionId;
        }
        const response = await axios.get(`${restUrl}/${originalId}/3d`, {
            params,
            headers: {
                'Content-Type': 'application/json',
                ...(catalogItemPage ? {catalogItemRequest: 'true'} : {}),
            },
        });
        const raw3dPayload = response.data;
        const sceneComposition = resolveCustomerCompositionSceneFrom3dGet(raw3dPayload);
        if (sceneComposition != null) {
            sceneComposition.texturedPictures = collectTexturedPicturesFromCustomer3dGet(raw3dPayload);
        }
        response.data = sceneComposition;
        return response;
    }

    static async recalculatePrice(id) {
        const response = await axios.post(`${restUrl}/${id}/recalculate-price`, null, {
            params: {
                key: getUid(),
            },
            headers: {
                'Content-Type': 'application/json',
            },
        });
        syncStoredUidFromCustomerResponse(response.data);
        return response;
    }

    static async save(data) {
        return await axios.post(restUrl, JSON.stringify(data), {
            headers: {
                'Content-Type': 'application/json'
            }
        });
    }

    static async edit(data, id) {
        return await axios.put(restUrl + '/' + id, JSON.stringify(data), {
            headers: {
                'Content-Type': 'application/json'
            }
        });
    }

    /** POST …/compositions/{originalId} — смена размеров модуля (query `uuid`, опционально `id` черновика). */
    static async module({originalId, id = null, data} = {}) {
        if (originalId == null || originalId === "" || data == null) {
            return Promise.reject(new Error("Invalid customer composition module POST"));
        }
        const params = {
            uuid: getUid(),
        };
        if (id != null && id !== "") {
            params.id = id;
        }

        const response = await axios.post(`${restUrl}/${originalId}`, data, {
            params,
            headers: {
                'Content-Type': 'application/json',
            },
            catalogModulePost: true,
        });
        syncStoredUidFromCustomerResponse(response.data);
        return response;
    }

    static async createOrder({originalId, id = null} = {}) {
        const params = {
            key: getUid(),
        };
        if (id != null && id !== "") {
            params.id = id;
        }

        const response = await axios.post(`${restUrl}/${originalId}/create-order`, null, {
            params,
            headers: {
                'Content-Type': 'application/json',
            },
        });
        syncStoredUidFromCustomerResponse(response.data);
        return response;
    }

    static async downloadContract(orderId) {
        return axios.get(`${restUrl}/files/download/contract`, {
            params: {orderId},
            responseType: 'blob',
        });
    }
}